import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container, Box, Typography, Paper, Button, Grid, TextField, MenuItem,
    Select, FormControl, InputLabel, Chip, OutlinedInput, Checkbox, ListItemText,
    Tabs, Tab, Divider, List, ListItem, ListItemIcon, Avatar, IconButton, InputBase,
    Dialog, DialogTitle, DialogContent, DialogActions, Stack, Autocomplete, Card, CardContent,
    ListItemSecondaryAction, CircularProgress, Grow, Table, TableBody, TableCell, TableHead, TableRow, Tooltip
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import WarningIcon from '@mui/icons-material/Warning';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CategoryIcon from '@mui/icons-material/Category';
import FlagIcon from '@mui/icons-material/Flag';
import LabelIcon from '@mui/icons-material/Label';
import InfoIcon from '@mui/icons-material/Info';
import EmailIcon from '@mui/icons-material/Email';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ForumIcon from '@mui/icons-material/Forum';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import CommentIcon from '@mui/icons-material/Comment';
import QrCodeIcon from '@mui/icons-material/QrCode';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function TicketDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';

    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [itProjects, setItProjects] = useState([]); // Added state for IT Projects
    const [types, setTypes] = useState([]);
    const [countries, setCountries] = useState([]);
    const [tabValue, setTabValue] = useState(0);
    const [openAssignDialog, setOpenAssignDialog] = useState(false);
    const [assignUser, setAssignUser] = useState('');
    const [assigningUser, setAssigningUser] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Chat State
    const [chatMessages, setChatMessages] = useState([]);
    const [newChatMessage, setNewChatMessage] = useState('');

    // Needs State
    const [needs, setNeeds] = useState([]);
    const [newNeed, setNewNeed] = useState({ description: '', priority: 'Medium' });
    const [openNeedDialog, setOpenNeedDialog] = useState(false);
    const [openNeedCommentDialog, setOpenNeedCommentDialog] = useState(false);
    const [currentNeed, setCurrentNeed] = useState(null);
    const [needComment, setNeedComment] = useState('');

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        problem: '',
        recommendation: '',
        user_recommendation: '',
        status: 'Open',
        requested_by: '',
        sender_email: '',
        on_behalf_name: '',
        on_behalf_email: '',
        project_id: '',
        ticket_type_id: '',
        assigned_to_ids: [],
        priority: 'Medium',
        resolution: '',
        due_date: '',
        done_at: '',
        created_at: ''
    });

    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('user_id');
    const headers = { Authorization: `Bearer ${token}` };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Done': return 'success';
            case 'In Progress': return 'info';
            case 'Open': return 'warning';
            case 'Reopen': return 'error';
            default: return 'default';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'Critical': return 'error';
            case 'High': return 'warning';
            case 'Medium': return 'info';
            case 'Low': return 'success';
            default: return 'default';
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [usersRes, projectsRes, itProjectsRes, typesRes, countriesRes] = await Promise.all([
                    axios.get(`${API_URL}/team`, { headers }),
                    axios.get(`${API_URL}/projects`, { headers }),
                    axios.get(`${API_URL}/it-projects`, { headers }),
                    axios.get(`${API_URL}/ticket-types`, { headers }),
                    axios.get(`${API_URL}/setup/countries`, { headers })
                ]);

                setUsers(usersRes.data);
                setProjects(projectsRes.data);
                setItProjects(itProjectsRes.data);
                setTypes(typesRes.data);
                setCountries(countriesRes.data);

                if (!isNew) {
                    const ticketRes = await axios.get(`${API_URL}/tickets/${id}`, { headers });
                    const attRes = await axios.get(`${API_URL}/tickets/${id}/attachments`, { headers });
                    setAttachments(attRes.data);

                    const chatRes = await axios.get(`${API_URL}/tickets/${id}/chat`, { headers });
                    setChatMessages(chatRes.data);

                    const needsRes = await axios.get(`${API_URL}/tickets/${id}/needs`, { headers });
                    setNeeds(needsRes.data);

                    const t = ticketRes.data;

                    // Helper to convert UTC string to Local ISO string for datetime-local input
                    const toLocal = (s) => {
                        if (!s) return '';
                        // Append Z if missing to treat as UTC
                        const d = new Date(s.endsWith('Z') ? s : s + 'Z');
                        const offset = d.getTimezoneOffset() * 60000;
                        return new Date(d.getTime() - offset).toISOString().slice(0, 16);
                    };

                    setFormData({
                        title: t.title,
                        description: t.description || '',
                        problem: t.problem || '',
                        recommendation: t.recommendation || '',
                        user_recommendation: t.user_recommendation || '',
                        status: t.status,
                        requested_by: t.requested_by || '',
                        sender_email: t.sender_email || '',
                        on_behalf_name: t.on_behalf_name || '',
                        on_behalf_email: t.on_behalf_email || '',
                        project_id: t.project_id || '',
                        it_project_id: t.it_project_id || '', // Added it_project_id
                        ticket_type_id: t.ticket_type_id || '',
                        country_id: t.country_id || '',
                        assigned_to_ids: t.assigned_to_ids || [],
                        priority: t.priority || 'Medium',
                        resolution: t.resolution || '',
                        due_date: t.due_date || '',
                        done_at: toLocal(t.done_at),
                        created_at: toLocal(t.created_at)
                    });
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    const handleAssignUser = async () => {
        if (assignUser && !formData.assigned_to_ids.includes(assignUser)) {
            const newAssignments = [...formData.assigned_to_ids, assignUser];
            setFormData({
                ...formData,
                assigned_to_ids: newAssignments
            });

            if (!isNew) {
                setAssigningUser(true);
                try {
                    await axios.put(`${API_URL}/tickets/${id}`, { assigned_to_ids: newAssignments }, { headers });
                } catch (err) {
                    console.error("Error updating assignments:", err);
                    alert("Failed to update assignments");
                } finally {
                    setAssigningUser(false);
                }
            }
        }
        setOpenAssignDialog(false);
        setAssignUser('');
    };

    const handleRemoveUser = async (userId) => {
        const newAssignments = formData.assigned_to_ids.filter(id => id !== userId);
        setFormData({
            ...formData,
            assigned_to_ids: newAssignments
        });

        if (!isNew) {
            try {
                await axios.put(`${API_URL}/tickets/${id}`, { assigned_to_ids: newAssignments }, { headers });
            } catch (err) {
                console.error("Error updating assignments:", err);
                alert("Failed to update assignments");
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let newFormData = { ...formData, [name]: value };

        if (name === 'status') {
            if (value === 'Done') {
                // Set done_at to current local datetime (YYYY-MM-DDTHH:mm)
                const now = new Date();
                now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
                newFormData.done_at = now.toISOString().slice(0, 16);
            } else {
                newFormData.done_at = '';
            }
        }
        setFormData(newFormData);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleUpload = async (file) => {
        if (!file) return;

        const uploadData = new FormData();
        uploadData.append('file', file);
        setUploading(true);

        try {
            await axios.post(`${API_URL}/tickets/${id}/attachments`, uploadData, {
                headers: { ...headers, 'Content-Type': 'multipart/form-data' }
            });
            const res = await axios.get(`${API_URL}/tickets/${id}/attachments`, { headers });
            setAttachments(res.data);
        } catch (err) {
            console.error(err);
            const msg = err.response?.data?.error || 'Failed to upload file';
            alert(msg);
        } finally {
            setUploading(false);
        }
    };

    const handleFileChange = (event) => {
        handleUpload(event.target.files[0]);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleUpload(files[0]);
        }
    };

    const handleAddChat = async () => {
        if (!newChatMessage.trim()) return;
        try {
            await axios.post(`${API_URL}/tickets/${id}/chat`, { message: newChatMessage }, { headers });
            setNewChatMessage('');
            const res = await axios.get(`${API_URL}/tickets/${id}/chat`, { headers });
            setChatMessages(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleAddNeed = async () => {
        try {
            await axios.post(`${API_URL}/tickets/${id}/needs`, newNeed, { headers });
            setOpenNeedDialog(false);
            setNewNeed({ description: '', priority: 'Medium' });
            const res = await axios.get(`${API_URL}/tickets/${id}/needs`, { headers });
            setNeeds(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleUpdateNeedStatus = async (needId, newStatus) => {
        try {
            await axios.put(`${API_URL}/tickets/needs/${needId}`, { status: newStatus }, { headers });
            const res = await axios.get(`${API_URL}/tickets/${id}/needs`, { headers });
            setNeeds(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleSendNeedComment = async () => {
        try {
            await axios.post(`${API_URL}/tickets/needs/${currentNeed.id}/comments`, { comment: needComment }, { headers });
            setOpenNeedCommentDialog(false);
            setNeedComment('');
            const res = await axios.get(`${API_URL}/tickets/${id}/needs`, { headers });
            setNeeds(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const [isGeneratingUserRecommendation, setIsGeneratingUserRecommendation] = useState(false);

    const generateUserRecommendation = async () => {
        setIsGeneratingUserRecommendation(true);
        try {
            const response = await fetch(`${API_URL}/tickets/rephrase`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...headers
                },
                body: JSON.stringify({
                    problem: formData.problem || '',
                    user_recommendation_input: formData.user_recommendation || ''
                })
            });

            const data = await response.json();
            if (data.status === 'success' && data.result?.rephrased_recommendation) {
                setFormData(prev => ({
                    ...prev,
                    user_recommendation: data.result.rephrased_recommendation
                }));
            }
        } catch (error) {
            console.error('Failed to generate user recommendation:', error);
        } finally {
            setIsGeneratingUserRecommendation(false);
        }
    };

    const [isSendingRecommendation, setIsSendingRecommendation] = useState(false);

    const handleSendRecommendationEmail = async () => {
        setIsSendingRecommendation(true);
        try {
            const response = await axios.post(`${API_URL}/tickets/${id}/send-recommendation`, {}, { headers });
            alert('Email sent successfully!');
        } catch (error) {
            console.error('Failed to send recommendation email:', error);
            alert(error.response?.data?.error || 'Failed to send email');
        } finally {
            setIsSendingRecommendation(false);
        }
    };

    const handleSave = async () => {
        try {
            const payload = { ...formData };

            // Convert local done_at back to UTC before sending
            if (payload.done_at) {
                const localDate = new Date(payload.done_at);
                payload.done_at = localDate.toISOString();
            }

            if (isNew) {
                payload.created_by_id = userId;
                await axios.post(`${API_URL}/tickets`, payload, { headers });
                navigate('/tickets');
            } else {
                await axios.put(`${API_URL}/tickets/${id}`, payload, { headers });
                // Keep in edit mode
            }
        } catch (err) {
            alert('Error saving ticket');
            console.error(err);
        }
    };

    if (loading) return <Loading />;

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            <Grow in={true} timeout={500}>
                <Box>
                    {/* Header Section */}
                    <Paper elevation={0} sx={{
                        mb: 4, 
                        mt: 2, 
                        p: 4, 
                        borderRadius: 4, 
                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
                        color: 'white',
                        boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {/* Background Decorative Circles */}
                        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                        <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

                        <Box sx={{ position: 'relative', zIndex: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'flex-start' }, justifyContent: 'space-between', gap: 3 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Button
                                        startIcon={<ArrowBackIcon />}
                                        onClick={() => navigate('/tickets')}
                                        sx={{ color: '#90caf9', mb: 2, pl: 0, fontWeight: 'bold', '&:hover': { color: 'white', bgcolor: 'transparent', transform: 'translateX(-4px)' }, transition: 'all 0.2s' }}
                                    >
                                        Back to Tickets
                                    </Button>
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                        <Avatar sx={{ 
                                            width: 52, height: 52, flexShrink: 0,
                                            bgcolor: 'rgba(255,255,255,0.15)', 
                                            color: '#e3f2fd', 
                                            boxShadow: '0 6px 18px rgba(0,0,0,0.2)',
                                            border: '2px solid rgba(255,255,255,0.2)',
                                        }}>
                                            <AssignmentIcon sx={{ color: 'white', fontSize: 26 }} />
                                        </Avatar>
                                        <InputBase
                                            value={formData.title}
                                            name="title"
                                            onChange={handleChange}
                                            placeholder="Ticket Title"
                                            sx={{
                                                color: 'white',
                                                fontSize: '1.25rem',
                                                fontWeight: '900',
                                                letterSpacing: 0.3,
                                                lineHeight: 1.3,
                                                '& .MuiInputBase-input': { p: 0, minWidth: '200px' }
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ml: 0.5 }}>
                                                <Chip
                                                    icon={<InfoIcon sx={{ fontSize: '1rem !important' }} />}
                                                    label={formData.status}
                                                    size="small"
                                                    sx={{ bgcolor: formData.status === 'Done' ? '#4caf50' : 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 700, borderRadius: 1.5, backdropFilter: 'blur(4px)' }}
                                                />
                                                {!isNew && (
                                                    <Chip
                                                        icon={<QrCodeIcon sx={{ fontSize: '1rem !important' }} />}
                                                        label={`Ref: ACTS - T - ${String(id).padStart(5, '0')}`}
                                                        size="small"
                                                        sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 600, borderRadius: 1.5, backdropFilter: 'blur(4px)' }}
                                                    />
                                                )}
                                                {formData.requested_by && (
                                                    <Chip
                                                        icon={<PersonIcon sx={{ fontSize: '1rem !important' }} />}
                                                        label={`Req: ${formData.requested_by}`}
                                                        size="small"
                                                        sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 600, borderRadius: 1.5, backdropFilter: 'blur(4px)' }}
                                                    />
                                                )}
                                                {formData.created_at && (
                                                    <Chip
                                                        icon={<EventIcon sx={{ fontSize: '1rem !important' }} />}
                                                        label={new Date(formData.created_at).toLocaleDateString()}
                                                        size="small"
                                                        sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 600, borderRadius: 1.5, backdropFilter: 'blur(4px)' }}
                                                    />
                                                )}
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row', width: { xs: '100%', md: 'auto' }, alignItems: 'center' }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSave}
                                        startIcon={<SaveIcon />}
                                        sx={{
                                            borderRadius: 2,
                                            bgcolor: 'white',
                                            color: '#1e3c72',
                                            fontWeight: 'bold',
                                            px: 3,
                                            boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                                            '&:hover': { 
                                                bgcolor: '#f0f0f0',
                                                transform: 'translateY(-2px)'
                                            },
                                            transition: 'all 0.2s',
                                            height: 'fit-content'
                                        }}
                                    >
                                        Save Changes
                                    </Button>

                                    {!isNew && (
                                        <Box sx={{ 
                                            bgcolor: 'rgba(255,255,255,0.1)', 
                                            backdropFilter: 'blur(10px)',
                                            p: 1.5, 
                                            borderRadius: 3, 
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            textAlign: 'center',
                                        }}>
                                            <Box sx={{ bgcolor: 'white', p: 1, borderRadius: 2 }}>
                                                <img
                                                    src={`${API_URL}/tickets/${id}/qr`}
                                                    alt="QR Code"
                                                    style={{ width: 100, height: 100, display: 'block', borderRadius: 4 }}
                                                />
                                            </Box>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem', mt: 1, display: 'block', fontWeight: 'bold' }}>
                                                SCAN TICKET
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Main Content Paper */}
                    <Grow in={true} timeout={800}>
                        <Box>
                            <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid #f0f0f0', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', mb: 4, bgcolor: 'white' }}>
                                <Tabs
                                value={tabValue}
                                onChange={handleTabChange}
                                variant="scrollable"
                                scrollButtons="auto"
                                TabIndicatorProps={{ sx: { height: 3, borderRadius: '3px 3px 0 0' } }} sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#fafafa', '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', minHeight: 64, fontSize: '0.95rem' } }}
                            >
                                <Tab icon={<AssignmentIcon />} label="Ticket Details" iconPosition="start" sx={{ minHeight: 64 }} />
                                <Tab icon={<PersonAddIcon />} label="Assign To" iconPosition="start" sx={{ minHeight: 64 }} />
                                <Tab icon={<CheckCircleIcon />} label="Resolution" iconPosition="start" sx={{ minHeight: 64 }} />
                                {!isNew && <Tab icon={<AttachFileIcon />} label="Documents" iconPosition="start" sx={{ minHeight: 64 }} />}
                                {!isNew && <Tab icon={<ForumIcon />} label="Chat" iconPosition="start" sx={{ minHeight: 64 }} />}
                                {!isNew && <Tab icon={<FlagIcon />} label="Needs" iconPosition="start" sx={{ minHeight: 64 }} />}
                            </Tabs>

                            {/* Tab 1: Ticket Details */}
                            <TabPanel value={tabValue} index={0}>
                                <Grid container spacing={3}>
                                    {/* Left Column: Description */}
                                    <Grid item xs={12} md={8}>
                                        <Card elevation={0} sx={{ borderRadius: 4, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                                            <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                                    <DescriptionIcon sx={{ color: '#3b82f6' }} />
                                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Ticket Description</Typography>
                                                </Box>
                                                
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    minRows={6}
                                                    name="description"
                                                    value={formData.description}
                                                    onChange={handleChange}
                                                    placeholder="Enter detailed ticket description..."
                                                    variant="outlined"
                                                    sx={{
                                                        mb: 3,
                                                        '& .MuiOutlinedInput-root': {
                                                            bgcolor: '#f8fafc',
                                                            borderRadius: 3,
                                                            border: '1px solid #e2e8f0',
                                                            padding: '16px',
                                                            fontFamily: 'system-ui, -apple-system, sans-serif',
                                                            lineHeight: 1.6,
                                                            color: '#334155',
                                                            fontSize: '0.95rem',
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            whiteSpace: 'pre-wrap',
                                                        }
                                                    }}
                                                />

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Problem</Typography>
                                                </Box>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    minRows={4}
                                                    name="problem"
                                                    value={formData.problem}
                                                    onChange={handleChange}
                                                    placeholder="Describe the problem..."
                                                    variant="outlined"
                                                    sx={{
                                                        mb: 3,
                                                        '& .MuiOutlinedInput-root': {
                                                            bgcolor: '#f8fafc',
                                                            borderRadius: 3,
                                                            border: '1px solid #e2e8f0',
                                                        }
                                                    }}
                                                />

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Recommendation</Typography>
                                                </Box>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    minRows={4}
                                                    name="recommendation"
                                                    value={formData.recommendation}
                                                    onChange={handleChange}
                                                    placeholder="Provide a recommendation..."
                                                    variant="outlined"
                                                    sx={{
                                                        mb: 3,
                                                        '& .MuiOutlinedInput-root': {
                                                            bgcolor: '#f8fafc',
                                                            borderRadius: 3,
                                                            border: '1px solid #e2e8f0',
                                                        }
                                                    }}
                                                />

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', flexGrow: 1 }}>User Recommendation</Typography>
                                                    
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={isSendingRecommendation ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
                                                        onClick={handleSendRecommendationEmail}
                                                        disabled={isSendingRecommendation || !formData.user_recommendation}
                                                        sx={{ 
                                                            borderRadius: 2,
                                                            textTransform: 'none',
                                                            bgcolor: '#10b981',
                                                            color: 'white',
                                                            boxShadow: '0 4px 6px -1px rgb(16 185 129 / 0.1), 0 2px 4px -2px rgb(16 185 129 / 0.1)',
                                                            '&:hover': {
                                                                bgcolor: '#059669',
                                                            }
                                                        }}
                                                    >
                                                        {isSendingRecommendation ? 'Sending...' : 'Send Recommendation via Email'}
                                                    </Button>

                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        startIcon={isGeneratingUserRecommendation ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
                                                        onClick={generateUserRecommendation}
                                                        disabled={isGeneratingUserRecommendation || !formData.problem}
                                                        sx={{ 
                                                            borderRadius: 2,
                                                            textTransform: 'none',
                                                            bgcolor: '#6366f1',
                                                            boxShadow: '0 4px 6px -1px rgb(99 102 241 / 0.1), 0 2px 4px -2px rgb(99 102 241 / 0.1)',
                                                            '&:hover': {
                                                                bgcolor: '#4f46e5',
                                                            }
                                                        }}
                                                    >
                                                        {isGeneratingUserRecommendation ? 'Generating...' : 'Rephrase'}
                                                    </Button>
                                                </Box>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    minRows={4}
                                                    name="user_recommendation"
                                                    value={formData.user_recommendation}
                                                    onChange={handleChange}
                                                    placeholder="Provide a user recommendation..."
                                                    variant="outlined"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            bgcolor: '#f8fafc',
                                                            borderRadius: 3,
                                                            border: '1px solid #e2e8f0',
                                                        }
                                                    }}
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {/* Right Column: Meta Data */}
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
                                            
                                            {/* Classification Card */}
                                            <Card elevation={0} sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                                                <CardContent sx={{ p: 4 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                                        <AssignmentIcon sx={{ color: '#2563eb' }} />
                                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Classification</Typography>
                                                    </Box>
                                                    
                                                    <Stack spacing={2}>
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Status</InputLabel>
                                                            <Select name="status" value={formData.status} label="Status" onChange={handleChange} sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                                                                <MenuItem value="Open"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} /> Open</Box></MenuItem>
                                                                <MenuItem value="In Progress"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'info.main' }} /> In Progress</Box></MenuItem>
                                                                <MenuItem value="Reopen"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} /> Reopen</Box></MenuItem>
                                                                <MenuItem value="Done"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} /> Done</Box></MenuItem>
                                                            </Select>
                                                        </FormControl>

                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Priority</InputLabel>
                                                            <Select name="priority" value={formData.priority} label="Priority" onChange={handleChange} sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                                                                <MenuItem value="Low">Low</MenuItem>
                                                                <MenuItem value="Medium">Medium</MenuItem>
                                                                <MenuItem value="High">High</MenuItem>
                                                                <MenuItem value="Critical">Critical</MenuItem>
                                                            </Select>
                                                        </FormControl>

                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Type</InputLabel>
                                                            <Select name="ticket_type_id" value={formData.ticket_type_id} label="Type" onChange={handleChange} sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                                                                {types.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                                                            </Select>
                                                        </FormControl>
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Country</InputLabel>
                                                            <Select name="country_id" value={formData.country_id} label="Country" onChange={handleChange} sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                                                                <MenuItem value=""><em>None</em></MenuItem>
                                                                {countries.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                                                            </Select>
                                                        </FormControl>
                                                    </Stack>
                                                </CardContent>
                                            </Card>

                                            {/* Full Context Card */}
                                            <Card elevation={0} sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                                                <CardContent sx={{ p: 4 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                                        <BusinessIcon sx={{ color: '#8b5cf6' }} />
                                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Full Context</Typography>
                                                    </Box>
                                                    
                                                    <Stack spacing={2}>
                                                        <Autocomplete
                                                            fullWidth
                                                            size="small"
                                                            options={projects}
                                                            getOptionLabel={(option) => option.name || ''}
                                                            value={projects.find(p => p.id === formData.project_id) || null}
                                                            onChange={(event, newValue) => {
                                                                setFormData({
                                                                    ...formData,
                                                                    project_id: newValue ? newValue.id : ''
                                                                });
                                                            }}
                                                            renderInput={(params) => <TextField {...params} label="Related Project" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }} />}
                                                        />
                                                        <Autocomplete
                                                            fullWidth
                                                            size="small"
                                                            options={itProjects}
                                                            getOptionLabel={(option) => option.name || ''}
                                                            value={itProjects.find(p => p.id === formData.it_project_id) || null}
                                                            onChange={(event, newValue) => {
                                                                setFormData({
                                                                    ...formData,
                                                                    it_project_id: newValue ? newValue.id : ''
                                                                });
                                                            }}
                                                            renderInput={(params) => <TextField {...params} label="Related IT Project" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }} />}
                                                        />
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Due Date"
                                                            type="date"
                                                            name="due_date"
                                                            value={formData.due_date}
                                                            onChange={handleChange}
                                                            InputLabelProps={{ shrink: true }}
                                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
                                                        />
                                                        {!isNew && (
                                                            <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 3, border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                <Avatar sx={{ bgcolor: '#dcfce7', color: '#22c55e', width: 36, height: 36 }}>
                                                                    <EventIcon fontSize="small" />
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="caption" color="#166534" fontWeight="bold" textTransform="uppercase" display="block">Created On</Typography>
                                                                    <Typography variant="body2" fontWeight="800" color="#15803d">
                                                                        {new Date(formData.created_at).toLocaleDateString()}
                                                                        <Typography component="span" variant="caption" sx={{ ml: 1, fontWeight: 'bold' }}>
                                                                            {new Date(formData.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </Typography>
                                                            </Typography>
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                </CardContent>
                                            </Card>

                                            {/* Contact Info Card */}
                                            <Card elevation={0} sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                                                <CardContent sx={{ p: 4 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                                        <PersonIcon sx={{ color: '#f59e0b' }} />
                                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Contact Info</Typography>
                                                    </Box>
                                                    
                                                    <Stack spacing={2}>
                                                        <Autocomplete
                                                            freeSolo
                                                            fullWidth
                                                            size="small"
                                                            options={users}
                                                            getOptionLabel={(option) => option.name || option}
                                                            value={users.find(u => u.name === formData.requested_by) || formData.requested_by}
                                                            onChange={async (event, newValue) => {
                                                                if (typeof newValue === 'string') {
                                                                    setFormData({ ...formData, requested_by: newValue });
                                                                    // Try to fetch from Azure AD if email-like
                                                                    if (newValue.includes('@')) {
                                                                        try {
                                                                            const res = await axios.get(`${API_URL}/tickets/check-ad-user?email=${newValue}`, { headers });
                                                                            if (res.data.found && res.data.country_id) {
                                                                                setFormData(prev => ({ ...prev, country_id: res.data.country_id }));
                                                                            }
                                                                        } catch (e) { console.log("AD Check failed", e); }
                                                                    }
                                                                } else if (newValue) {
                                                                    let newCountryId = formData.country_id;
                                                                    if (newValue.countries && newValue.countries.length > 0) {
                                                                        const cName = newValue.countries[0];
                                                                        const cObj = countries.find(c => c.name === cName);
                                                                        if (cObj) newCountryId = cObj.id;
                                                                    }
                                                                    setFormData({
                                                                        ...formData,
                                                                        requested_by: newValue.name,
                                                                        country_id: newCountryId
                                                                    });
                                                                } else {
                                                                    setFormData({ ...formData, requested_by: '' });
                                                                }
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Requested By"
                                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        startAdornment: (
                                                                            <>
                                                                                <PersonIcon color="action" fontSize="small" sx={{ mr: 1, ml: 1 }} />
                                                                                {params.InputProps.startAdornment}
                                                                            </>
                                                                        )
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Contact Email"
                                                            name="sender_email"
                                                            value={formData.sender_email}
                                                            onChange={handleChange}
                                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
                                                            InputProps={{
                                                                startAdornment: <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>@</Typography>
                                                            }}
                                                        />
                                                        <Autocomplete
                                                            freeSolo
                                                            fullWidth
                                                            size="small"
                                                            options={users}
                                                            getOptionLabel={(option) => option.name || option}
                                                            value={users.find(u => u.name === formData.on_behalf_name) || formData.on_behalf_name}
                                                            onChange={async (event, newValue) => {
                                                                if (typeof newValue === 'string') {
                                                                    setFormData({ ...formData, on_behalf_name: newValue });
                                                                } else if (newValue) {
                                                                    setFormData({
                                                                        ...formData,
                                                                        on_behalf_name: newValue.name
                                                                    });
                                                                } else {
                                                                    setFormData({ ...formData, on_behalf_name: '' });
                                                                }
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="On Behalf Name"
                                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        startAdornment: (
                                                                            <>
                                                                                <PersonIcon color="action" fontSize="small" sx={{ mr: 1, ml: 1 }} />
                                                                                {params.InputProps.startAdornment}
                                                                            </>
                                                                        )
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="On Behalf Email"
                                                            name="on_behalf_email"
                                                            value={formData.on_behalf_email}
                                                            onChange={handleChange}
                                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
                                                            InputProps={{
                                                                startAdornment: <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>@</Typography>
                                                            }}
                                                        />
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                            
                                        </Box>
                                    </Grid>
                                </Grid>
                            </TabPanel>

                            {/* Tab 2: Assign To */}
                            <TabPanel value={tabValue} index={1}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>Assigned Team Members</Typography>
                                    <Button 
                                        variant="contained" 
                                        startIcon={<PersonAddIcon />} 
                                        onClick={() => setOpenAssignDialog(true)}
                                        sx={{ 
                                            borderRadius: 2, 
                                            fontWeight: 'bold', 
                                            px: 3, 
                                            boxShadow: '0 4px 14px rgba(25,118,210,0.3)',
                                            background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)',
                                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' }
                                        }}
                                    >
                                        Add Member
                                    </Button>
                                </Box>
                                <Grid container spacing={3}>
                                    {formData.assigned_to_ids.map((id) => {
                                        const member = users.find(u => u.id === id);
                                        if (!member) return null;
                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={member.id}>
                                                <Card 
                                                    elevation={0}
                                                    sx={{ 
                                                        borderRadius: 4,
                                                        border: '1px solid #f1f5f9',
                                                        transition: 'all 0.2s ease-in-out',
                                                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px -4px rgba(148, 163, 184, 0.15)', borderColor: '#e2e8f0' }
                                                    }}
                                                >
                                                    <CardContent sx={{ p: 3 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                                                            <Avatar sx={{ bgcolor: '#eff6ff', color: '#0ea5e9', fontWeight: 800, width: 56, height: 56, border: '2px solid white', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                                                {member.name ? member.name[0].toUpperCase() : '?'}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5 }} noWrap>
                                                                    {member.name}
                                                                </Typography>
                                                                <Chip label={member.role || 'Member'} size="small" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 800, bgcolor: '#f1f5f9', color: '#475569', borderRadius: 1.5 }} />
                                                            </Box>
                                                        </Box>
                                                        
                                                        <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
                                                        
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => handleRemoveUser(member.id)} 
                                                                sx={{ bgcolor: '#fef2f2', color: '#ef4444', '&:hover': { bgcolor: '#fee2e2', color: '#b91c1c' } }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                    {formData.assigned_to_ids.length === 0 && (
                                        <Grid item xs={12}>
                                            <Paper elevation={0} sx={{ p: 8, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 4, border: '2px dashed #cbd5e1' }}>
                                                <PersonAddIcon sx={{ fontSize: 64, color: '#94a3b8', mb: 2, opacity: 0.5 }} />
                                                <Typography variant="h6" sx={{ color: '#475569', fontWeight: 700, mb: 1 }}>No team members assigned</Typography>
                                                <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>Add members to collaborate on this ticket.</Typography>
                                                <Button variant="outlined" onClick={() => setOpenAssignDialog(true)} sx={{ borderRadius: 2, fontWeight: 700, borderWidth: 2, '&:hover': { borderWidth: 2 } }}>
                                                    Assign Someone
                                                </Button>
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                            </TabPanel>

                            {/* Tab 3: Resolution */}
                            <TabPanel value={tabValue} index={2}>
                                <Card elevation={0} sx={{ borderRadius: 4, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                                    <CardContent sx={{ p: 4 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                            <CheckCircleIcon sx={{ color: '#10b981' }} />
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Resolution Details</Typography>
                                        </Box>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={8}
                                                    label="Resolution Notes"
                                                    name="resolution"
                                                    value={formData.resolution}
                                                    onChange={handleChange}
                                                    placeholder="Describe how the ticket was resolved..."
                                                    variant="outlined"
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    type="datetime-local"
                                                    label="Completed On"
                                                    name="done_at"
                                                    value={formData.done_at}
                                                    InputProps={{ readOnly: true }}
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Duration"
                                                    value={(() => {
                                                        if (!formData.created_at || !formData.done_at) return '';
                                                        const start = new Date(formData.created_at);
                                                        const end = new Date(formData.done_at);
                                                        const diff = end - start;
                                                        if (diff < 0) return 'Invalid';
                                                        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                                                        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                                                        return `${days}d ${hours}h`;
                                                    })()}
                                                    InputProps={{ readOnly: true }}
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </TabPanel>

                            {/* Tab 3: Documents */}
                            {!isNew && (
                                <TabPanel value={tabValue} index={3}>
                                    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="h6" fontWeight="800" color="#1e293b">Documents</Typography>
                                        <Button
                                            variant="contained"
                                            component="label"
                                            disabled={uploading}
                                            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                            sx={{ 
                                                borderRadius: 2, 
                                                px: 3, 
                                                height: 42, 
                                                whiteSpace: 'nowrap',
                                                fontWeight: 'bold',
                                                background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)',
                                                boxShadow: '0 4px 14px rgba(25,118,210,0.3)',
                                                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' }
                                            }}
                                        >
                                            {uploading ? 'Uploading...' : 'Upload File'}
                                            <input
                                                type="file"
                                                hidden
                                                onChange={handleFileChange}
                                            />
                                        </Button>
                                    </Box>

                                    <Box
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        sx={{
                                            border: '2px dashed',
                                            borderColor: isDragging ? '#3b82f6' : '#cbd5e1',
                                            borderRadius: 4,
                                            p: 4,
                                            mb: 4,
                                            bgcolor: isDragging ? '#eff6ff' : '#f8fafc',
                                            textAlign: 'center',
                                            transition: 'all 0.2s',
                                            cursor: 'pointer',
                                            '&:hover': { borderColor: '#94a3b8', bgcolor: '#f1f5f9' }
                                        }}
                                    >
                                        <CloudUploadIcon sx={{ fontSize: 48, color: isDragging ? '#3b82f6' : '#94a3b8', mb: 1 }} />
                                        <Typography variant="subtitle1" color="#1e293b" fontWeight="700">
                                            {isDragging ? 'Drop file to upload' : 'Drag and drop file here'}
                                        </Typography>
                                        <Typography variant="caption" color="#64748b" fontWeight="500">
                                            or click the upload button above
                                        </Typography>
                                    </Box>

                                    <Grow in={true} timeout={800}>
                                        <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', width: '100%', bgcolor: 'white', mb: 4 }}>
                                            <Box sx={{ overflowX: 'auto', width: '100%', display: 'block' }}>
                                                <Table sx={{ width: '100%', tableLayout: 'auto', '& .MuiTableCell-root': { borderColor: '#f0f0f0', py: 2 } }} aria-label="documents table">
                                                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>File Name</TableCell>
                                                            <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Uploaded By / Date</TableCell>
                                                            <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', align: 'right' }}>Actions</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {attachments && attachments.length === 0 && (
                                                            <TableRow>
                                                                <TableCell colSpan={3} align="center" sx={{ py: 6, color: '#94a3b8', fontWeight: 600 }}>
                                                                    No documents uploaded yet.
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                        {attachments && attachments.map((file) => (
                                                            <TableRow 
                                                                key={file.id} 
                                                                hover 
                                                                sx={{ 
                                                                    '&:last-child td, &:last-child th': { border: 0 }, 
                                                                    transition: 'background-color 0.2s',
                                                                    '&:hover': { bgcolor: '#f1f5f9 !important' }
                                                                }}
                                                            >
                                                                <TableCell>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                        <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', width: 36, height: 36 }}>
                                                                            <InsertDriveFileIcon fontSize="small" />
                                                                        </Avatar>
                                                                        <Typography variant="body2" fontWeight="700" color="#1e293b" title={file.filename}>
                                                                            {file.filename}
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Box>
                                                                        <Typography variant="body2" color="#1e293b" fontWeight="600">{file.uploaded_by}</Typography>
                                                                        <Typography variant="caption" color="#64748b" fontWeight="500">
                                                                            {new Date(file.uploaded_at).toLocaleDateString()}
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Tooltip title="Download">
                                                                        <IconButton 
                                                                            href={`${API_URL}/tickets/attachments/${file.id}`} 
                                                                            size="small" 
                                                                            sx={{ color: '#16a34a', '&:hover': { bgcolor: '#f0fdf4' } }}
                                                                        >
                                                                            <DownloadIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                        </Paper>
                                    </Grow>
                                </TabPanel>
                            )}

                            {/* Tab 4: Chat */}
                            {!isNew && (
                                <TabPanel value={tabValue} index={4}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, alignItems: 'center' }}>
                                        <Typography variant="h6" fontWeight="bold" color="#1e293b">Ticket Discussion</Typography>
                                        <Typography variant="body2" color="#64748b" sx={{ bgcolor: '#f1f5f9', px: 2, py: 0.5, borderRadius: 2, fontWeight: 600 }}>
                                            Live Team Chat
                                        </Typography>
                                    </Box>

                                    <Grow in={true} timeout={1200}>
                                        <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column', height: 600, borderRadius: 4, overflow: 'hidden', border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', bgcolor: 'white', mb: 4 }}>
                                            <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: '#f1f5f9', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar sx={{ bgcolor: '#e0e7ff', color: '#2563eb' }}><ForumIcon /></Avatar>
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="800" color="#1e293b">General Channel</Typography>
                                                    <Typography variant="caption" color="#64748b" fontWeight="500">Coordinate and share updates with the project team.</Typography>
                                                </Box>
                                            </Box>

                                            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {chatMessages && chatMessages.length === 0 && (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5 }}>
                                                        <ForumIcon sx={{ fontSize: 64, color: '#94a3b8', mb: 2 }} />
                                                        <Typography variant="h6" color="#475569" fontWeight="700">No messages yet</Typography>
                                                        <Typography variant="body2" color="#64748b">Break the ice! Start the conversation.</Typography>
                                                    </Box>
                                                )}
                                                {chatMessages && chatMessages.map((msg, idx) => {
                                                    const isCurrentUser = msg.user_name === localStorage.getItem('username');
                                                    return (
                                                        <Box key={msg.id || idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: isCurrentUser ? 'flex-end' : 'flex-start' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, px: 0.5, flexDirection: isCurrentUser ? 'row-reverse' : 'row' }}>
                                                                <Avatar sx={{ width: 24, height: 24, fontSize: 11, fontWeight: 'bold', bgcolor: isCurrentUser ? '#2563eb' : '#cbd5e1' }}>
                                                                    {msg.user_name ? msg.user_name[0].toUpperCase() : '?'}
                                                                </Avatar>
                                                                <Typography variant="caption" fontWeight="700" color="#475569">{msg.user_name}</Typography>
                                                                <Typography variant="caption" color="#94a3b8" fontWeight="500">
                                                                    • {new Date(msg.timestamp).toLocaleString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}
                                                                </Typography>
                                                            </Box>
                                                            <Paper elevation={0} sx={{ 
                                                                p: 1.5, 
                                                                px: 2.5,
                                                                bgcolor: isCurrentUser ? '#2563eb' : 'white', 
                                                                color: isCurrentUser ? 'white' : '#1e293b',
                                                                borderRadius: 3, 
                                                                borderTopLeftRadius: isCurrentUser ? 16 : 4,
                                                                borderTopRightRadius: isCurrentUser ? 4 : 16,
                                                                border: isCurrentUser ? 'none' : '1px solid #e2e8f0', 
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                                                maxWidth: '75%' 
                                                            }}>
                                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontWeight: 500 }}>{msg.message}</Typography>
                                                            </Paper>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>

                                            <Box sx={{ p: 2.5, bgcolor: 'white', borderTop: '1px solid #f1f5f9' }}>
                                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        variant="outlined"
                                                        placeholder="Type your message..."
                                                        value={newChatMessage}
                                                        onChange={e => setNewChatMessage(e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && handleAddChat()}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 3,
                                                                bgcolor: '#f8fafc',
                                                                transition: 'all 0.2s',
                                                                '& fieldset': { borderColor: '#e2e8f0' },
                                                                '&:hover fieldset': { borderColor: '#cbd5e1' },
                                                                '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: 2 },
                                                            }
                                                        }}
                                                    />
                                                    <IconButton
                                                        onClick={handleAddChat}
                                                        disabled={!newChatMessage.trim()}
                                                        sx={{
                                                            bgcolor: newChatMessage.trim() ? '#2563eb' : '#f1f5f9',
                                                            color: newChatMessage.trim() ? 'white' : '#94a3b8',
                                                            width: 44, 
                                                            height: 44,
                                                            transition: 'all 0.2s',
                                                            '&:hover': { bgcolor: newChatMessage.trim() ? '#1d4ed8' : '#f1f5f9', transform: newChatMessage.trim() ? 'scale(1.05)' : 'none' },
                                                            '&.Mui-disabled': { bgcolor: '#f8fafc', color: '#cbd5e1' }
                                                        }}
                                                    >
                                                        <SendIcon fontSize="small" sx={{ ml: 0.5 }} />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grow>
                                </TabPanel>
                            )}

                            {/* Tab 5: Needs */}
                            {!isNew && (
                                <TabPanel value={tabValue} index={5}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, alignItems: 'center' }}>
                                        <Typography variant="h6" fontWeight="bold" color="#1e293b">Requirements</Typography>
                                        <Button 
                                            variant="contained" 
                                            onClick={() => setOpenNeedDialog(true)} 
                                            startIcon={<AddIcon />} 
                                            sx={{ 
                                                borderRadius: 2, 
                                                px: 3, 
                                                height: 42, 
                                                whiteSpace: 'nowrap',
                                                fontWeight: 'bold',
                                                background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)',
                                                boxShadow: '0 4px 14px rgba(25,118,210,0.3)',
                                                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' }
                                            }}
                                        >
                                            Add Requirement
                                        </Button>
                                    </Box>

                                    <Grow in={true} timeout={1200}>
                                        <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', width: '100%', bgcolor: 'white', mb: 4 }}>
                                            <Box sx={{ overflowX: 'auto', width: '100%', display: 'block' }}>
                                                <Table sx={{ width: '100%', tableLayout: 'auto', '& .MuiTableCell-root': { borderColor: '#f0f0f0', py: 2 } }} aria-label="needs table">
                                                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                                        <TableRow>
                                                            <TableCell sx={{ width: 60 }}></TableCell>
                                                            <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Requirement</TableCell>
                                                            <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Priority</TableCell>
                                                            <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Dates</TableCell>
                                                            <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', align: 'right' }}>Actions</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {needs && needs.length === 0 && (
                                                            <TableRow>
                                                                <TableCell colSpan={5} align="center" sx={{ py: 6, color: '#94a3b8', fontWeight: 600 }}>
                                                                    No requirements listed yet.
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                        {needs && needs.map((need, index) => (
                                                            <TableRow 
                                                                key={need.id} 
                                                                hover 
                                                                sx={{ 
                                                                    '&:last-child td, &:last-child th': { border: 0 }, 
                                                                    bgcolor: need.status === 'Met' ? '#f8fafc' : 'inherit',
                                                                    transition: 'background-color 0.2s',
                                                                    '&:hover': { bgcolor: '#f1f5f9 !important' }
                                                                }}
                                                            >
                                                                <TableCell sx={{ pl: 3 }}>
                                                                    <IconButton size="small" onClick={() => handleUpdateNeedStatus(need.id, need.status === 'Met' ? 'Pending' : 'Met')} sx={{ color: need.status === 'Met' ? '#22c55e' : '#cbd5e1', '&:hover': { color: need.status === 'Met' ? '#16a34a' : '#94a3b8', bgcolor: 'transparent' } }}>
                                                                        {need.status === 'Met' ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                                                                    </IconButton>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body1" sx={{ textDecoration: need.status === 'Met' ? 'line-through' : 'none', color: need.status === 'Met' ? '#94a3b8' : '#1e293b', fontWeight: need.status === 'Met' ? 500 : 700 }}>
                                                                        {need.description}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={need.priority}
                                                                        size="small"
                                                                        color={need.priority === 'High' ? 'error' : need.priority === 'Medium' ? 'warning' : 'default'}
                                                                        variant="outlined"
                                                                        sx={{ height: 24, fontSize: '0.75rem', border: 'none', bgcolor: 'grey.100' }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                        {need.created_at && (
                                                                            <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <AccessTimeIcon sx={{ fontSize: 14 }} /> Created: {new Date(need.created_at).toLocaleDateString()}
                                                                            </Typography>
                                                                        )}
                                                                        {need.due_date && (
                                                                            <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <EventIcon sx={{ fontSize: 14 }} /> Due: {new Date(need.due_date).toLocaleDateString()}
                                                                            </Typography>
                                                                        )}
                                                                        {need.status === 'Met' && need.done_at && (
                                                                            <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <CheckCircleIcon sx={{ fontSize: 14 }} /> Completed: {new Date(need.done_at).toLocaleDateString()}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ pr: 3 }}>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                                        <Tooltip title="Comments">
                                                                            <IconButton size="small" onClick={() => {
                                                                                setCurrentNeed(need);
                                                                                setOpenNeedCommentDialog(true);
                                                                            }} sx={{ bgcolor: need.comments && need.comments.length > 0 ? '#eff6ff' : 'transparent', color: need.comments && need.comments.length > 0 ? '#3b82f6' : '#94a3b8', '&:hover': { bgcolor: '#dbeafe', color: '#2563eb' } }}>
                                                                                <CommentIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Box>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                        </Paper>
                                    </Grow>
                                </TabPanel>
                            )}

                        </Paper>

                        <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 500, overflow: 'visible' } }}>
                            <DialogTitle sx={{ fontWeight: 800, pb: 2, fontSize: '1.25rem', color: '#1e293b' }}>
                                Assign User
                            </DialogTitle>
                            <DialogContent dividers sx={{ borderColor: '#f1f5f9', py: 3, pt: 4, overflow: 'visible' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                        <InputLabel>User</InputLabel>
                                        <Select 
                                            value={assignUser} 
                                            onChange={(e) => setAssignUser(e.target.value)} 
                                            label="User"
                                        >
                                            {users.map(u => (
                                                <MenuItem key={u.id} value={u.id}>{u.name} ({u.role || 'Member'})</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                                <Button 
                                    onClick={() => setOpenAssignDialog(false)} 
                                    variant="outlined" 
                                    sx={{ borderRadius: 2, fontWeight: 'bold', borderColor: '#cbd5e1', color: '#475569', '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' } }}
                                >
                                    CANCEL
                                </Button>
                                <Button 
                                    onClick={handleAssignUser} 
                                    variant="contained" 
                                    disabled={assigningUser} 
                                    sx={{ 
                                        borderRadius: 2, 
                                        fontWeight: 'bold', 
                                        minWidth: 100,
                                        boxShadow: 'none',
                                        bgcolor: '#3b82f6',
                                        color: 'white',
                                        '&:hover': { bgcolor: '#2563eb', boxShadow: 'none' }
                                    }}
                                >
                                    {assigningUser ? <CircularProgress size={24} color="inherit" /> : 'ASSIGN'}
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <Dialog open={openNeedDialog} onClose={() => setOpenNeedDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 500, overflow: 'visible' } }}>
                            <DialogTitle sx={{ fontWeight: 800, pb: 2, fontSize: '1.25rem', color: '#1e293b' }}>Add Requirement</DialogTitle>
                            <DialogContent dividers sx={{ borderColor: '#f1f5f9', py: 3, pt: 4, overflow: 'visible' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Requirement Description"
                                        placeholder="Describe the need..."
                                        value={newNeed.description}
                                        onChange={e => setNewNeed({ ...newNeed, description: e.target.value })}
                                        multiline rows={3}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                    <FormControl fullWidth>
                                        <InputLabel>Priority</InputLabel>
                                        <Select
                                            value={newNeed.priority}
                                            label="Priority"
                                            onChange={e => setNewNeed({ ...newNeed, priority: e.target.value })}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        >
                                            <MenuItem value="Low">Low</MenuItem>
                                            <MenuItem value="Medium">Medium</MenuItem>
                                            <MenuItem value="High">High</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        fullWidth
                                        label="Due Date"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={newNeed.due_date || ''}
                                        onChange={e => setNewNeed({ ...newNeed, due_date: e.target.value })}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                                <Button 
                                    onClick={() => setOpenNeedDialog(false)} 
                                    variant="outlined" 
                                    sx={{ borderRadius: 2, fontWeight: 'bold', borderColor: '#cbd5e1', color: '#475569', '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' } }}
                                >
                                    CANCEL
                                </Button>
                                <Button 
                                    onClick={handleAddNeed} 
                                    variant="contained" 
                                    disabled={!newNeed.description}
                                    sx={{ 
                                        borderRadius: 2, 
                                        fontWeight: 'bold', 
                                        minWidth: 100,
                                        boxShadow: 'none',
                                        bgcolor: '#3b82f6',
                                        color: 'white',
                                        '&:hover': { bgcolor: '#2563eb', boxShadow: 'none' }
                                    }}
                                >
                                    ADD
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {/* Need Comment Dialog */}
                        <Dialog open={openNeedCommentDialog} onClose={() => setOpenNeedCommentDialog(false)} maxWidth="sm" fullWidth >
                            <DialogTitle>
                                Requirement Comments
                                <Typography variant="caption" display="block" color="text.secondary">{currentNeed?.description}</Typography>
                            </DialogTitle>
                            <DialogContent dividers>
                                <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
                                    {needs && currentNeed && needs.find(n => n.id === currentNeed.id)?.comments && needs.find(n => n.id === currentNeed.id)?.comments.length === 0 && (
                                        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>No comments yet.</Typography>
                                    )}
                                    {needs && currentNeed && needs.find(n => n.id === currentNeed.id)?.comments?.map(comment => (
                                        <Box key={comment.id} sx={{ mb: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                <Typography variant="subtitle2" fontWeight="bold">{comment.user_name}</Typography>
                                                <Typography variant="caption" color="text.secondary">{new Date(comment.created_at).toLocaleString()}</Typography>
                                            </Box>
                                            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                                                <Typography variant="body2">{comment.comment}</Typography>
                                            </Paper>
                                        </Box>
                                    ))}
                                </Box>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    placeholder="Add a comment..."
                                    value={needComment}
                                    onChange={(e) => setNeedComment(e.target.value)}
                                    variant="outlined"
                                    size="small"
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenNeedCommentDialog(false)}>Close</Button>
                                <Button onClick={handleSendNeedComment} variant="contained" disabled={!needComment.trim()} endIcon={<SendIcon />}>Send</Button>
                            </DialogActions>
                        </Dialog>
                        </Box>
                    </Grow>
                </Box>
            </Grow>
        </Box>
    );
}

export default TicketDetails;
